import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PatientService from "../services/PatientService";
import DoctorService from "../services/DoctorService";
import VisitService from "../services/VisitService";
import AcupunctureCardService from "../services/AcupunctureCardService";
import moment from "moment";

function NewVisitForm() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pacijent
  const [phone, setPhone] = useState("");
  const [patient, setPatient] = useState(null);
  const [patientError, setPatientError] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // Doktor
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [doctorError, setDoctorError] = useState("");

  // Vrsta pregleda
  const [treatmentType, setTreatmentType] = useState("Auriculotherapy");

  // Parametri tretmana
  const [auriculoParams, setAuriculoParams] = useState({
    weight: "",
    bmi: "",
    waist: "",
    bodyFat: "",
    visceralFat: "",
    protein: "",
    hipCircumference: "",
    abdomenCircumference: "",
    chestCircumference: "",
    dietPlan: ""
  });
  const [aestheticParams, setAestheticParams] = useState({ procedureDescription: "" });

  // Karton akupunkture (za Telesnu akupunkturu)
  const [acupunctureCards, setAcupunctureCards] = useState([]);
  const [selectedAcupunctureCard, setSelectedAcupunctureCard] = useState("");

  // Upozorenje: obimi nisu upisani u poslednje tri kontrole aurikuloterapije
  const [showObimiWarning, setShowObimiWarning] = useState(false);

  // Napomena i datum
  const [notes, setNotes] = useState("");
  const [visitDate, setVisitDate] = useState(moment().format("YYYY-MM-DDTHH:mm"));



  // NOVO: Automatsko prepoznavanje prosleđenog pacijenta
  useEffect(() => {
    if (location.state && location.state.selectedPatient) {
      const p = location.state.selectedPatient;
      setPatient(p);
      setPhone(`${p.name} ${p.surname} (${p.phone || ""})`);
    }
  }, [location]);

  // Učitaj doktore
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const data = await DoctorService.getAll();
        if (Array.isArray(data)) setDoctors(data);
        else setDoctorError("Nema dostupnih doktora");
      } catch (err) {
        console.error(err);
        setDoctorError(`Neuspešno učitavanje doktora: ${err.message}`);
      }
    };
    loadDoctors();
  }, []);

  // Predlozi pacijenata
  useEffect(() => {
    // Ne pokreći pretragu ako je pacijent već selektovan (iz navigacije)
    if (!phone || (patient && phone === `${patient.name} ${patient.surname} (${patient.phone || ""})`)) {
      setSuggestions([]);
      return;
    }
    const loadSuggestions = async () => {
      try {
        const res = await PatientService.getAll();
        const data = res.data || res;
        const filtered = data.filter(p =>
          p.phone?.includes(phone) ||
          p.name.toLowerCase().includes(phone.toLowerCase()) ||
          p.surname.toLowerCase().includes(phone.toLowerCase())
        );
        setSuggestions(filtered);
      } catch (err) {
        console.error(err);
      }
    };
    loadSuggestions();
  }, [phone, patient]);

  const handleSelectSuggestion = (p) => {
    setPatient(p);
    setPhone(`${p.name} ${p.surname} (${p.phone})`);
    setSuggestions([]);
  };

  // Učitaj kartone akupunkture za pacijenta kad se izabere "Telesna akupunktura"
  useEffect(() => {
    if (treatmentType !== "BodyAcupuncture" || !patient) {
      setAcupunctureCards([]);
      setSelectedAcupunctureCard("");
      return;
    }
    AcupunctureCardService.getByPatientId(patient.patientID)
      .then((cards) => setAcupunctureCards(Array.isArray(cards) ? cards : []))
      .catch((err) => console.error("Greška pri učitavanju kartona akupunkture:", err));
  }, [treatmentType, patient]);

  // Upozorenje ako NI U JEDNOJ od poslednje 3 kontrole aurikuloterapije nije upisano 2+ obima (struk/kukovi/stomak/grudi)
  useEffect(() => {
    if (treatmentType !== "Auriculotherapy" || !patient) {
      setShowObimiWarning(false);
      return;
    }
    let cancelled = false;
    VisitService.getAllVisitsByPatientId(patient.patientID)
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;

        const isFilled = (v) => v !== null && v !== undefined && v !== 0;

        const lastThree = data
          .filter(v => (v.visitItemDTOS || []).some(i => i.treatmentTypeDTO?.typeName === "Auriculotherapy"))
          .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate))
          .slice(0, 3)
          .map(v => v.visitItemDTOS.find(i => i.treatmentTypeDTO?.typeName === "Auriculotherapy")?.treatmentTypeDTO);

        // Upozorenje samo ako NIJEDNA od poslednje 3 kontrole nema 2 ili više upisanih obima
        const noneSufficient = lastThree.length > 0 && lastThree.every(d => {
          const filledCount = [d?.waist, d?.hipCircumference, d?.abdomenCircumference, d?.chestCircumference]
            .filter(isFilled).length;
          return filledCount < 2;
        });

        setShowObimiWarning(noneSufficient);
      })
      .catch((err) => {
        console.error("Greška pri proveri istorije obima:", err);
        setShowObimiWarning(false);
      });
    return () => { cancelled = true; };
  }, [treatmentType, patient]);

  const buildTreatmentTypeDTO = (type) => {
    const base = { typeName: type, notes };
    switch (type) {
      case "Auriculotherapy":
        return {
          ...base,
          weight: auriculoParams.weight ? Number(auriculoParams.weight) : null,
          bmi: auriculoParams.bmi ? Number(auriculoParams.bmi) : null,
          waist: auriculoParams.waist ? Number(auriculoParams.waist) : null,
          bodyFat: auriculoParams.bodyFat ? Number(auriculoParams.bodyFat) : null,
          visceralFat: auriculoParams.visceralFat ? Number(auriculoParams.visceralFat) : null,
          protein: auriculoParams.protein ? Number(auriculoParams.protein) : null,
          hipCircumference: auriculoParams.hipCircumference ? Number(auriculoParams.hipCircumference) : null,
          abdomenCircumference: auriculoParams.abdomenCircumference ? Number(auriculoParams.abdomenCircumference) : null,
          chestCircumference: auriculoParams.chestCircumference ? Number(auriculoParams.chestCircumference) : null,
          dietPlan: auriculoParams.dietPlan || null, 
        };
      case "AestheticMedicine":
        return {
          ...base,
          procedureDescription: aestheticParams.procedureDescription || null,
        };
      case "BodyAcupuncture":
        return {
          ...base,
          acupuncturePoints: null,
          patientState: null,
        };
      default:
        return base;
    }
  };

 const handleSave = async () => {
  if (!patient) { alert("Pacijent nije odabran!"); return; }
  if (!selectedDoctor) { alert("Doktor nije odabran!"); return; }
  if (treatmentType === "BodyAcupuncture" && !selectedAcupunctureCard) {
    alert("Izaberite karton akupunkture (postojeći ili 'Novi karton')!");
    return;
  }

  const visitItemDTOS = [{ treatmentTypeDTO: buildTreatmentTypeDTO(treatmentType) }];

  let acupunctureCardID = null;
  if (treatmentType === "BodyAcupuncture") {
    if (selectedAcupunctureCard === "new") {
      try {
        const created = await AcupunctureCardService.create({
          patientDTO: { patientID: Number(patient.patientID) },
          examDate: moment(visitDate).format("YYYY-MM-DD"),
        });
        acupunctureCardID = created.cardID;
      } catch (err) {
        alert(`Greška pri kreiranju kartona akupunkture: ${err.message}`);
        return;
      }
    } else {
      acupunctureCardID = Number(selectedAcupunctureCard);
    }
  }

  const visitDTO = {
    visitID: null,
    status: "WAITING",
    // IZMENA: Šaljemo lokalni format koji Backend (Jackson) razume, bez UTC konverzije
    visitDate: moment(visitDate).format("YYYY-MM-DDTHH:mm:ss"),
    notes,
    doctorDTO: { doctorID: Number(selectedDoctor) },
    patientDTO: { patientID: Number(patient.patientID) },
    visitItemDTOS,
    acupunctureCardID,
  };

  try {
    await VisitService.createVisit(visitDTO);
    alert("Poseta uspešno sačuvana!");
    navigate(`/pacijenti`);
  } catch (err) {
    console.error(err);
    alert(`Greška prilikom čuvanja posete: ${err.message}`);
  }
};

  return (
    <div style={{
      maxWidth: "800px", margin: "20px auto", padding: "20px",
      borderRadius: "10px", backgroundColor: "#fdfdfd",
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
    }}>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>Nova Poseta</h2>

      {doctorError && <p style={{ color: "red" }}>{doctorError}</p>}
      {patientError && <p style={{ color: "red" }}>{patientError}</p>}

      {/* Pacijent sekcija */}
      <div style={{ marginBottom: "15px", position: "relative" }}>
        <label><b>Pacijent (Pretraga po telefonu/imenu):</b></label>
        <input
          type="text"
          placeholder="Unesite podatke o pacijentu..."
          value={phone}
          onChange={(e) => { setPhone(e.target.value); setPatient(null); }}
          style={{
            width: "100%", padding: "10px", marginTop: "5px",
            borderRadius: "6px", border: "1px solid #ccc"
          }}
        />
        {suggestions.length > 0 && (
          <ul style={{
            position: "absolute", top: "100%", left: 0, right: 0,
            background: "#fff", border: "1px solid #ccc", borderRadius: "5px",
            maxHeight: "200px", overflowY: "auto", zIndex: 1000,
            listStyle: "none", padding: 0, boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}>
            {suggestions.slice(0, 10).map(s => (
              <li
                key={s.patientID}
                onClick={() => handleSelectSuggestion(s)}
                style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "#fff"}
              >
                {s.name} {s.surname} ({s.phone})
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Doktor i Datum */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "15px" }}>
        <div style={{ flex: 1 }}>
          <label><b>Doktor:</b></label>
          <select
            value={selectedDoctor}
            onChange={(e) => setSelectedDoctor(e.target.value)}
            style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
          >
            <option value="">-- Odaberite doktora --</option>
            {doctors.map((doc) => (
              <option key={doc.doctorID} value={doc.doctorID}>
                {doc.firstName} {doc.lastName}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label><b>Datum i vreme:</b></label>
          <input
            type="datetime-local"
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value)}
            style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
        </div>
      </div>

      {/* Vrsta pregleda */}
      <div style={{ marginBottom: "15px" }}>
        <label><b>Vrsta pregleda:</b></label>
        <select
          value={treatmentType}
          onChange={(e) => setTreatmentType(e.target.value)}
          style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
        >
          <option value="Auriculotherapy">Aurikuloterapija</option>
          <option value="AestheticMedicine">Estetska medicina</option>
          <option value="BodyAcupuncture">Telesna akupunktura</option>
        </select>
      </div>

      {/* PARAMETRI ZA AURIKULOTERAPIJU (Uslovno prikazivanje) */}
      {treatmentType === "Auriculotherapy" && (
        <div style={{ 
          marginBottom: "20px", padding: "15px", border: "1px solid #e0e0e0", 
          borderRadius: "8px", backgroundColor: "#fff" 
        }}>
          <h4 style={{ borderBottom: "1px solid #eee", paddingBottom: "5px", color: "#2c3e50" }}>
            Aurikuloterapija - Parametri:
          </h4>
          {showObimiWarning && (
            <div style={{
              display: "flex", alignItems: "center", gap: "10px",
              backgroundColor: "#fdecea", border: "1px solid #e74c3c",
              borderRadius: "8px", padding: "12px 15px", marginTop: "10px", marginBottom: "5px"
            }}>
              <span style={{ color: "#e74c3c", fontSize: "1.6rem", fontWeight: "bold" }}>❗</span>
              <span style={{ color: "#c0392b", fontWeight: "600" }}>
                Ni u jednoj od poslednje 3 kontrole ovog pacijenta nije upisano 2 ili više obima (struk, kukovi, stomak, grudi) — vreme je da se izmere.
              </span>
            </div>
          )}
          <div style={{
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", 
            gap: "15px",
            marginTop: "10px"
          }}>
            {[
              { key: "weight", label: "Težina (kg)" },
              { key: "bmi", label: "BMI" },
              { key: "waist", label: "Obim struka (cm)" },
              { key: "bodyFat", label: "Masti u telu %" },
              { key: "visceralFat", label: "Visceralne masti" },
              { key: "protein", label: "Proteini %" },
              { key: "hipCircumference", label: "Obim kukova (cm)" },
              { key: "abdomenCircumference", label: "Obim stomaka (cm)" },
              { key: "chestCircumference", label: "Obim grudi (cm)" }
            ].map((item) => (
              <div key={item.key}>
                <label style={{ fontSize: "13px" }}>{item.label}:</label>
                <input
                  type="number"
                  step="0.1"
                  value={auriculoParams[item.key]}
                  onChange={(e) => setAuriculoParams({ ...auriculoParams, [item.key]: e.target.value })}
                  style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ccc" }}
                />
              </div>
            ))}
          </div>

          {/* Polje za Jelovnik */}
          <div style={{ marginTop: "15px" }}>
            <label><b>Jelovnik / Beleške o ishrani:</b></label>
            <textarea
              rows="3"
              placeholder="Unesite plan ishrane ili savete..."
              value={auriculoParams.dietPlan}
              onChange={(e) => setAuriculoParams({ ...auriculoParams, dietPlan: e.target.value })}
              style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", marginTop: "5px" }}
            />
          </div>
        </div>
      )}

      {/* PARAMETRI ZA ESTETSKU MEDICINU */}
      {treatmentType === "AestheticMedicine" && (
        <div style={{
          marginBottom: "20px", padding: "15px", border: "1px solid #e0e0e0",
          borderRadius: "8px", backgroundColor: "#fff"
        }}>
          <h4 style={{ borderBottom: "1px solid #eee", paddingBottom: "5px", color: "#2c3e50" }}>
            Estetska medicina - Parametri:
          </h4>
          <label><b>Opis procedure:</b></label>
          <textarea
            rows="3"
            placeholder="Unesite opis procedure..."
            value={aestheticParams.procedureDescription}
            onChange={(e) => setAestheticParams({ ...aestheticParams, procedureDescription: e.target.value })}
            style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", marginTop: "5px" }}
          />
        </div>
      )}

      {/* PARAMETRI ZA TELESNU AKUPUNKTURU */}
      {treatmentType === "BodyAcupuncture" && (
        <div style={{
          marginBottom: "20px", padding: "15px", border: "1px solid #e0e0e0",
          borderRadius: "8px", backgroundColor: "#fff"
        }}>
          <h4 style={{ borderBottom: "1px solid #eee", paddingBottom: "5px", color: "#2c3e50" }}>
            Telesna akupunktura - Parametri:
          </h4>

          <label><b>Karton akupunkture:</b></label>
          <select
            value={selectedAcupunctureCard}
            onChange={(e) => setSelectedAcupunctureCard(e.target.value)}
            style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", marginBottom: "15px" }}
            disabled={!patient}
          >
            <option value="">-- Odaberite karton --</option>
            {acupunctureCards.map((c) => (
              <option key={c.cardID} value={c.cardID}>
                Karton #{c.cardID} {c.examDate ? `(${c.examDate})` : ""}
              </option>
            ))}
            <option value="new">➕ Novi karton (kreiraj automatski)</option>
          </select>
          <p style={{ fontSize: "0.85rem", color: "#777", marginTop: "5px" }}>
            Akupunkturne tačke i stanje pacijenta doktor unosi kasnije, iz kartona (kad pacijent uđe sa čekanja).
          </p>
        </div>
      )}

      {/* Globalna napomena posete */}
      <div style={{ marginBottom: "20px" }}>
        <label><b>Opšta napomena za posetu:</b></label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", minHeight: "60px" }}
        />
      </div>

      <button
        onClick={handleSave}
        style={{
          width: "100%", padding: "12px", borderRadius: "8px", border: "none",
          backgroundColor: "#28a745", color: "#fff", cursor: "pointer", fontSize: "16px", fontWeight: "bold"
        }}
      >
        Sačuvaj posetu i pošalji u čekaonicu
      </button>
    </div>
  );
}

export default NewVisitForm;